// src/merch/ScanImportDialog.jsx
//
// Upload a scanned / photographed colouring sheet → auto-find the black
// squares → (optionally drag the dots to fix) → straightened, cleaned
// drawing lands as a layer covering the whole sticker (incl. bleed).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { merch, addLayerRaw } from "./store";
import { getZone, printAreaFor, printSizeFor } from "./products";
import { sheetLayout, guideShapes } from "./colouringSheet";
import { detectMarkers, markerAspect, warpToSticker, cleanScan } from "./scanImport";

const MAX_SRC = 2200;   // working resolution for the photo
const DETECT_MAX = 1000;

const isHeic = (f) => /hei[cf]$/i.test(f.type || "") || /\.hei[cf]$/i.test(f.name || "");

// decode via <img> (Safari opens HEIC natively this way)
function decodeViaImg(blob) {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(blob);
    const im = new Image();
    im.onload = () => { res(im); URL.revokeObjectURL(url); };
    im.onerror = () => { rej(new Error("decode failed")); URL.revokeObjectURL(url); };
    im.src = url;
  });
}

// HEIC → JPEG for Chrome/Firefox. Loaded on demand from a CDN so it
// costs nothing unless someone actually uploads a HEIC.
async function heicToJpeg(file) {
  const mod = await import(/* @vite-ignore */ "https://esm.sh/heic2any@0.0.4");
  const heic2any = mod.default || mod;
  const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  return Array.isArray(out) ? out[0] : out;
}

const isPdf = (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");

// PDF (iPhone Notes/Files "Scan Documents", desktop scanners) → canvas of
// page 1. pdf.js is loaded on demand from cdnjs.
let pdfjsPromise = null;
function loadPdfJs() {
  if (!pdfjsPromise) {
    const V = "3.11.174";
    pdfjsPromise = new Promise((res, rej) => {
      if (window.pdfjsLib) return res(window.pdfjsLib);
      const sc = document.createElement("script");
      sc.src = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${V}/pdf.min.js`;
      sc.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${V}/pdf.worker.min.js`;
        res(window.pdfjsLib);
      };
      sc.onerror = () => { pdfjsPromise = null; rej(new Error("pdf.js failed to load")); };
      document.head.appendChild(sc);
    });
  }
  return pdfjsPromise;
}
async function pdfToCanvas(file) {
  const pdfjs = await loadPdfJs();
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const page = await doc.getPage(1);
  const v1 = page.getViewport({ scale: 1 });
  const scale = MAX_SRC / Math.max(v1.width, v1.height);
  const vp = page.getViewport({ scale });
  const c = document.createElement("canvas");
  c.width = Math.round(vp.width); c.height = Math.round(vp.height);
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  doc.destroy?.();
  return c;
}

async function decodeAny(file) {
  if (isPdf(file)) return pdfToCanvas(file);
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch { /* fall through */ }
  try {
    return await decodeViaImg(file);
  } catch { /* fall through */ }
  if (isHeic(file)) {
    const jpg = await heicToJpeg(file);
    return await createImageBitmap(jpg).catch(() => decodeViaImg(jpg));
  }
  throw new Error("unsupported image");
}

async function loadToCanvas(file) {
  const bmp = await decodeAny(file);
  const k = Math.min(1, MAX_SRC / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * k);
  c.height = Math.round(bmp.height * k);
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  bmp.close?.();
  if (!c.width || !c.height) throw new Error("empty image");
  return c;
}

function toImageData(canvas, maxSide) {
  const k = Math.min(1, maxSide / Math.max(canvas.width, canvas.height));
  const w = Math.round(canvas.width * k), h = Math.round(canvas.height * k);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, 0, 0, w, h);
  return { img: ctx.getImageData(0, 0, w, h), k };
}

function pixelsToCanvas(px) {
  const c = document.createElement("canvas");
  c.width = px.width; c.height = px.height;
  c.getContext("2d").putImageData(new ImageData(px.data, px.width, px.height), 0, 0);
  return c;
}

export default function ScanImportDialog({ file, product, size, onClose }) {
  const snap = useSnapshot(merch);
  const zone = getZone(product, size, snap.side, snap.hand);
  const sticker = zone.sticker;
  const area = printAreaFor(sticker);
  const layout = useMemo(() => sheetLayout(sticker), [sticker]);
  const guides = useMemo(() => guideShapes(sticker), [sticker]);
  // where to erase guides: ±2mm on paper around each printed line
  const cleanOpts = (outW) => ({ ...opts, guides, pxPerMm: outW / layout.BW, bandMm: Math.max(0.6, 2 / layout.s) });
  const [src, setSrc] = useState(null);         // working canvas
  const [srcUrl, setSrcUrl] = useState(null);
  const [corners, setCorners] = useState(null); // [TL,TR,BR,BL] in src px
  const [found, setFound] = useState(null);
  const [opts, setOpts] = useState({ removeGuides: true, seeThrough: false });
  const [dragging, setDragging] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const svgRef = useRef(null);
  const previewRef = useRef(null);
  const srcPixels = useRef(null);
  const sideLabel = zone.label;

  // load + detect
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await loadToCanvas(file);
        if (!alive) return;
        srcPixels.current = c.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, c.width, c.height);
        const { img, k } = toImageData(c, DETECT_MAX);
        const det = detectMarkers(img, { aspect: markerAspect(layout) });
        setSrc(c);
        setSrcUrl(c.toDataURL("image/jpeg", 0.85));
        if (det) {
          setCorners(det.corners.map(([x, y]) => [x / k, y / k]));
          setFound(det.oriented ? "oriented" : "found");
        } else {
          const iw = c.width, ih = c.height, m = 0.12;
          setCorners([[iw * m, ih * m], [iw * (1 - m), ih * m], [iw * (1 - m), ih * (1 - m)], [iw * m, ih * (1 - m)]]);
          setFound(false);
        }
      } catch (e) {
        console.error(e);
        setError(isPdf(file)
          ? "Couldn't read that PDF. Try exporting the scan as a JPG or PNG."
          : isHeic(file)
          ? "Couldn't open that HEIC photo here. Try opening this page in Safari, or export the photo as JPG."
          : "Couldn't open that image. Try a JPG or PNG photo.");
      }
    })();
    return () => { alive = false; };
  }, [file]);

  // live preview (skipped mid-drag to stay smooth)
  useEffect(() => {
    if (!corners || dragging !== -1 || !srcPixels.current) return;
    const out = warpToSticker(srcPixels.current, corners, layout, 640);
    if (!out) return;
    cleanScan(out, cleanOpts(out.width));
    const cv = previewRef.current;
    if (!cv) return;
    cv.width = out.width; cv.height = out.height;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.putImageData(new ImageData(out.data, out.width, out.height), 0, 0);
  }, [corners, opts, dragging, layout, guides]); // eslint-disable-line react-hooks/exhaustive-deps

  function svgPoint(e) {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return [Math.max(0, Math.min(src.width, p.x)), Math.max(0, Math.min(src.height, p.y))];
  }
  function onDown(i, e) {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(i);
  }
  function onMove(e) {
    if (dragging < 0) return;
    const p = svgPoint(e);
    setCorners((c) => c.map((q, j) => (j === dragging ? p : q)));
  }
  function onUp() { setDragging(-1); }

  async function importIt() {
    setBusy(true);
    try {
      const aw = area.x1 - area.x0, ah = area.y1 - area.y0;
      const awB = (aw * layout.BW) / sticker.widthMm;   // bleed rect in design units
      const ahB = (ah * layout.BH) / sticker.heightMm;
      const outW = Math.min(3072, Math.round(awB * printSizeFor(sticker)));
      const out = warpToSticker(srcPixels.current, corners, layout, outW);
      cleanScan(out, cleanOpts(out.width));
      const url = pixelsToCanvas(out).toDataURL("image/png");
      addLayerRaw(
        {
          name: "Colouring sheet",
          url,
          kind: "scan", // customer artwork — reviewed like an upload
          transform: { tx: (area.x0 + area.x1) / 2, ty: (area.y0 + area.y1) / 2, scale: Math.max(awB, ahB), rot: 0 },
        },
        { bottom: true }
      );
      onClose();
    } catch (e) {
      console.error(e);
      setError("Something went wrong importing — try another photo.");
    } finally {
      setBusy(false);
    }
  }

  const r = src ? Math.max(src.width, src.height) / 45 : 10;

  return (
    <div className="ma-modal">
      <div className="ma-modal-card ma-scan" role="dialog" aria-label="Upload colouring sheet">
        <div className="ma-modal-head">
          <b>Colouring sheet → {sideLabel}</b>
          <button className="ma-btn ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {error && <div className="ma-note error">{error}</div>}
        {!src && !error && <p className="ma-muted">Opening photo…</p>}

        {src && corners && (
          <>
            <p className={`ma-note ${found ? "" : "warn"}`}>
              {found
                ? found === "oriented"
                  ? "✓ Found the black squares and which way is up. Check the preview — drag a dot if it's off."
                  : "✓ Found the 4 black squares. If the preview is sideways or upside down, tap Rotate."
                : "Couldn't spot all 4 black squares. Drag each dot onto a black square."}
            </p>
            <div className="ma-scan-stage">
             <div className="ma-scan-box">
              <img src={srcUrl} alt="Your sheet" draggable={false} />
              <svg
                ref={svgRef}
                viewBox={`0 0 ${src.width} ${src.height}`}
                preserveAspectRatio="none"
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
              >
                <polygon className="ma-scan-quad" points={corners.map((p) => p.join(",")).join(" ")} />
                {corners.map(([x, y], i) => (
                  <g key={i}>
                    <circle cx={x} cy={y} r={r} className={`ma-scan-dot ${i === 0 ? "first" : ""}`} />
                    <circle cx={x} cy={y} r={r * 2.2} className="ma-scan-hit" onPointerDown={(e) => onDown(i, e)} />
                  </g>
                ))}
              </svg>
             </div>
            </div>

            <div className="ma-row ma-scan-tools">
              <h3>Preview</h3>
              <button
                className="ma-btn ghost"
                title="Rotate 90°"
                onClick={() => setCorners((c) => [...c.slice(1), c[0]])}
              >
                ↻ Rotate
              </button>
            </div>
            <div className="ma-scan-preview"><canvas ref={previewRef} /></div>

            <div className="ma-row">
              <label className="ma-check">
                <input type="checkbox" checked={opts.removeGuides} onChange={(e) => setOpts((o) => ({ ...o, removeGuides: e.target.checked }))} />
                <span>Remove the printed trim line</span>
              </label>
              <label className="ma-check">
                <input type="checkbox" checked={opts.seeThrough} onChange={(e) => setOpts((o) => ({ ...o, seeThrough: e.target.checked }))} />
                <span>See-through paper (only my drawing shows)</span>
              </label>
            </div>
            <p className="ma-muted">
              Use the sheet for this part ({product.name} · {size.label} · {sideLabel}).
              Best results: bright even light, no flash, no shadows on the page — or use your phone's document scanner.
            </p>

            <div className="ma-modal-foot">
              <button className="ma-btn primary" disabled={busy} onClick={importIt}>{busy ? "Adding…" : "Add to my design"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
